import { FieldValue, db as firestoreDb } from 'firebase-modules';
import {
  action,
  comparer,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import { monotonicFactory, ulid } from 'ulid';
import {
  Field,
  FilenameSettings,
  FirestoreSession,
  moduleColors,
  BaseSession,
  safeBatch,
  FirestoreSource,
  RowData,
  SessionPermissions,
  SessionUserPermission,
  FirestoreSessionPermission,
  FirestoreCard,
  waitFor,
  SessionModuleDisplaySettings,
  SessionModule,
  StepHistoryEvent,
  SessionStep,
} from '@creative-kit/shared';
import type firebase from 'firebase';
import type { RootStore } from '.';
import { db } from './data';
import { Card } from './card';
import { STEP_TAB_TRIAGE } from './ui-active-session';
import { emitCardEvent } from './card-events';
import {
  buildStepCardBuckets,
  getBatchMoveSuccessCount,
  getCardIdsWithStepMoveFirst,
  getMoveStepName,
} from './sessionStepUtils';
import { trackAction } from './analytics';

export class Session extends BaseSession {
  protected rootStore: RootStore;
  private ref;
  private commentsRef;
  cardListRef;
  permissionsRef;
  private _cacheUpdateDisposer?: () => void;
  private _permissionListener?: () => void;
  private _permissionsLoading = false;
  private _cachedInProgressVisibleCardIds: string[] = [];
  private _cachedDoneVisibleCardIds: string[] = [];

  constructor(id: string, data: FirestoreSession, rootStore: RootStore) {
    super(id, data, rootStore);

    makeObservable<
      this,
      | '_cachedInProgressVisibleCardIds'
      | '_cachedDoneVisibleCardIds'
      | 'userPermissions'
      | 'permissionsLoaded'
    >(this, {
      data: observable.struct,
      _cachedInProgressVisibleCardIds: observable.ref,
      _cachedDoneVisibleCardIds: observable.ref,
      userPermissions: observable,
      permissionsLoaded: observable,
      setModuleIds: action,
      reorderModule: action,
      updateData: action,
      updateCache: action,
      loadUserPermissions: action,
      modules: computed,
      automaticFilenameVersioning: computed,
      automaticCardTitleSuffix: computed,
      visibleModules: computed,
      inProgressCardIds: computed,
      currentCardIdx: computed,
      currentCardId: computed,
      upNextCardIdx: computed,
      onDeckCardIdx: computed,
      doneCount: computed,
      doneCardIds: computed,
      doneTotalCount: computed,
      doneVisibleCount: computed,
      inProgressCount: computed,
      inProgressTotalCount: computed,
      inProgressVisibleCount: computed,
      inProgressCardIdsByStep: computed,
      visibleInProgressCardIdsByStep: computed,
      cardList: computed,
      numFilenamesNeedingAttention: computed,
      isFilenameVisible: computed,
      canViewSession: computed,
      canEditSession: computed,
      arePermissionsLoaded: computed,
      setModuleDisplaySettings: action,
      batchSetModuleDisplaySettings: action,
      setStepModuleDisplaySettings: action,
      batchSetStepModuleDisplaySettings: action,
      setStepHiddenModuleIds: action,
      setStepCardTitleFormula: action,
      clearStepLayoutOverride: action,
      updateSessionDocument: action,
      // Step-related computed properties (from BaseSession)
      steps: computed,
      sortedSteps: computed,
      hasSteps: computed,
    });

    this.rootStore = rootStore;
    this.ref = db.sessions.doc(id);
    this.commentsRef = db.sessionComments(id);
    this.cardListRef = db.sessionCards(id);
    this.permissionsRef = db.sessionPermissions(id);

    // Setup cache update reaction
    this._setupCacheUpdateReaction();
  }

  get sessionRef() {
    return this.ref;
  }

  updateData(newData: FirestoreSession) {
    this.data = newData;
  }

  updateCache() {
    const {
      cardsStore: { cards },
    } = this.rootStore;

    // Calculate visible cards once for both lists
    const inProgressVisible: string[] = [];
    const doneVisible: string[] = [];

    this.inProgressCardIds.forEach((cardId) => {
      const card = cards[cardId];
      if (card?.isVisible) {
        inProgressVisible.push(cardId);
      }
    });

    this.doneCardIds.forEach((cardId) => {
      const card = cards[cardId];
      if (card?.isVisible) {
        doneVisible.push(cardId);
      }
    });

    this._cachedInProgressVisibleCardIds = inProgressVisible;
    this._cachedDoneVisibleCardIds = doneVisible;
  }

  get modules() {
    // Map moduleIds to modules, preserving 1-to-1 correspondence
    // Include undefined entries to maintain index alignment with moduleIds
    return (this.data?.moduleIds ?? [])
      .map((moduleId) => this.data?.modules?.[moduleId])
      .filter(Boolean);
  }

  get hiddenModules() {
    return this.modules.filter(
      (module): module is SessionModule =>
        module !== undefined && !this.getIsModuleVisible(module.id),
    );
  }

  get visibleModules() {
    return this.modules.filter(
      (module): module is SessionModule =>
        module !== undefined && this.getIsModuleVisible(module.id),
    );
  }

  /** Step-aware visible modules — checks step overrides first, then global. */
  getVisibleModulesForStep(stepId?: string | null): SessionModule[] {
    const hiddenIds = this.getEffectiveHiddenModuleIds(stepId);
    return this.modules.filter(
      (module): module is SessionModule => module !== undefined && !hiddenIds.includes(module.id),
    );
  }

  getSourceById(sourceId: string) {
    return this.rootStore.sourcesStore.sources[sourceId];
  }

  getModuleDisplaySettings(moduleId: string): SessionModuleDisplaySettings {
    return this.data.moduleDisplaySettings && this.data.moduleDisplaySettings[moduleId];
  }

  setModuleDisplaySettings(moduleId: string, settings: SessionModuleDisplaySettings) {
    this.ref.update({
      [`moduleDisplaySettings.${moduleId}`]: settings,
    });
  }

  batchSetModuleDisplaySettings(updates: Record<string, SessionModuleDisplaySettings>) {
    const updatePayload: Record<string, SessionModuleDisplaySettings> = {};
    for (const [moduleId, settings] of Object.entries(updates)) {
      updatePayload[`moduleDisplaySettings.${moduleId}`] = settings;
    }
    this.ref.update(updatePayload);
  }

  getEffectiveModuleDisplaySettings(
    moduleId: string,
    stepId?: string | null,
  ): SessionModuleDisplaySettings {
    if (stepId) {
      const stepSettings =
        this.data.stepLayoutOverrides?.[stepId]?.moduleDisplaySettings?.[moduleId];
      if (stepSettings) return stepSettings;
    }
    return this.getModuleDisplaySettings(moduleId);
  }

  getEffectiveHiddenModuleIds(stepId?: string | null): string[] {
    if (stepId) {
      const stepHidden = this.data.stepLayoutOverrides?.[stepId]?.hiddenModuleIds;
      if (stepHidden !== undefined) return stepHidden;
    }
    return this.data.visibility?.hiddenModuleIds ?? [];
  }

  setStepModuleDisplaySettings(
    stepId: string,
    moduleId: string,
    settings: SessionModuleDisplaySettings,
  ) {
    return this.ref.update({
      [`stepLayoutOverrides.${stepId}.moduleDisplaySettings.${moduleId}`]: settings,
    });
  }

  batchSetStepModuleDisplaySettings(
    stepId: string,
    updates: Record<string, SessionModuleDisplaySettings>,
  ) {
    const updatePayload: Record<string, SessionModuleDisplaySettings> = {};
    for (const [moduleId, settings] of Object.entries(updates)) {
      updatePayload[`stepLayoutOverrides.${stepId}.moduleDisplaySettings.${moduleId}`] = settings;
    }
    return this.ref.update(updatePayload);
  }

  setStepHiddenModuleIds(stepId: string, ids: string[]) {
    return this.ref.update({
      [`stepLayoutOverrides.${stepId}.hiddenModuleIds`]: ids,
    });
  }

  setStepCardTitleFormula(stepId: string, formula: Field[]) {
    return this.ref.update({
      [`stepLayoutOverrides.${stepId}.cardTitleFormula`]: formula,
    });
  }

  clearStepLayoutOverride(stepId: string) {
    return this.ref.update({
      [`stepLayoutOverrides.${stepId}`]: FieldValue.delete(),
    });
  }

  /**
   * Merged Firestore update for the session document (supports dotted paths like
   * `moduleDisplaySettings.${id}`). Used by layout UI when batching dynamic keys.
   */
  updateSessionDocument(updates: Record<string, unknown>) {
    if (Object.keys(updates).length === 0) {
      return Promise.resolve();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore UpdateData + dynamic paths
    return this.ref.update(updates as any);
  }

  public getVisible(list: string[]) {
    const {
      cardsStore: { cards },
    } = this.rootStore;

    return list.map((cardId) => cards[cardId]).filter((card) => card.isVisible);
  }

  private getVisibleCount(list: string[]) {
    return this.getVisible(list).length;
  }

  private getDisplayCount(list: string[]) {
    const numCards = list.length;

    if (!this.isFilteringInProgress) {
      return `${numCards}`;
    }

    const numVisibleCards = this.getVisibleCount(list);

    return `${numVisibleCards}/${numCards}`;
  }

  get currentCardId() {
    return this.data.cardIds[this.currentCardIdx];
  }

  get upNextCardIdx() {
    return this.currentCardIdx + 1;
  }

  get onDeckCardIdx() {
    return this.currentCardIdx + 2;
  }

  get inProgressVisibleCardIds() {
    if (!this.isFilteringInProgress) {
      return this.inProgressCardIds;
    }
    return this._cachedInProgressVisibleCardIds;
  }

  get inProgressVisibleCount() {
    if (!this.isFilteringInProgress) {
      return this.inProgressCardIds.length;
    }
    return this._cachedInProgressVisibleCardIds.length;
  }

  get isFilteringInProgress() {
    const { hasSearch } = this.rootStore.uiCardSearchStore;
    const { hasRowSelection } = this.rootStore.uiModulePowerColumnStore;
    const { hasAnyQuickFilters } = this.rootStore.uiActiveSessionStore;
    return hasSearch || hasRowSelection || hasAnyQuickFilters;
  }

  get doneVisibleCardIds() {
    if (!this.isFilteringInProgress) {
      return this.doneCardIds;
    }
    return this._cachedDoneVisibleCardIds;
  }

  get doneVisibleCount() {
    if (!this.isFilteringInProgress) {
      return this.doneCardIds.length;
    }
    return this._cachedDoneVisibleCardIds.length;
  }

  get inProgressCardIdsByStep() {
    return buildStepCardBuckets({
      cardIds: this.data.cardIds ?? [],
      steps: this.steps,
      cards: this.rootStore.cardsStore.cards,
    }).inProgressByStep;
  }

  get visibleInProgressCardIdsByStep() {
    return buildStepCardBuckets({
      cardIds: this.data.cardIds ?? [],
      steps: this.steps,
      cards: this.rootStore.cardsStore.cards,
      visibleOnly: true,
    }).inProgressByStep;
  }

  get inProgressTotalCount() {
    return this.inProgressCardIds.length;
  }

  get doneTotalCount() {
    return this.doneCardIds.length;
  }

  get inProgressCount() {
    return this.getDisplayCount(this.inProgressCardIds);
  }

  get doneCount() {
    return this.getDisplayCount(this.doneCardIds);
  }

  get numFilenamesNeedingAttention() {
    return (this.cardList as Card[]).filter((s) => s.filenameNeedsAttention).length;
  }

  get isFilenameVisible() {
    const { visibility } = this.data;
    return visibility ? visibility?.hideFilename === false : true;
  }

  get includeFilenameInQueueSearch() {
    const { setting } = this.rootStore.uiCardSearchStore;
    return setting.type === 'all' || (setting.type === 'visible' && this.isFilenameVisible);
  }

  get includeTitleInQueueSearch() {
    const { setting } = this.rootStore.uiCardSearchStore;
    return setting.type === 'all' || setting.type === 'visible';
  }

  get includeMessagesInQueueSearch() {
    const { setting } = this.rootStore.uiCardSearchStore;
    return (setting.type === 'all' || setting.type === 'visible') && setting.messages;
  }

  getIsModuleVisible = (id: string) =>
    Boolean(this.data.visibility?.hiddenModuleIds?.includes(id)) === false;

  // Back-end operations
  acceptAllFilenames = () => {
    (this.cardList as Card[]).forEach((s) => s.acceptNewFilename());
  };

  ignoreAllFilenames = () => {
    (this.cardList as Card[]).forEach((s) => s.ignoreNewFilename());
  };

  setName = (newName: string) =>
    this.ref.set(
      {
        name: newName.trim(),
      },
      {
        merge: true,
      },
    );

  setModuleIds(moduleIds: string[]) {
    return this.ref.set(
      {
        moduleIds,
      },
      { merge: true },
    );
  }

  reorderModule(dragIndex: number, hoverIndex: number) {
    const newModuleIds = [...this.data.moduleIds];
    const draggedModuleId = newModuleIds[dragIndex];

    // Remove the dragged item
    newModuleIds.splice(dragIndex, 1);
    // Insert it at the new position
    newModuleIds.splice(hoverIndex, 0, draggedModuleId);

    return this.setModuleIds(newModuleIds);
  }

  setSourceIds(sourceIds: string[]) {
    return this.ref.set(
      {
        sourceIds,
      },
      { merge: true },
    );
  }

  createComment(cardId: string, text: string) {
    const { user } = this.rootStore.authStore;
    if (!user) {
      return;
    }

    if (text) {
      this.commentsRef.add({
        cardId,
        text,
        userId: user.uid,
        avatarUrl: user.avatarUrl,
        displayName: user.displayName || 'anonymous',
        createdAt: FieldValue.serverTimestamp() as unknown as Date,
        updatedAt: FieldValue.serverTimestamp() as unknown as Date,
      });
    }
  }

  private addConnectionOnly = async ({
    rowIds,
    moduleId,
    card,
    position,
    afterCardId,
  }: {
    rowIds: string | string[];
    moduleId: string;
    card?: Card;
    position?: 'start' | 'end';
    afterCardId?: string;
  }): Promise<{ undo: () => Promise<void>; newCardId: string | undefined } | undefined> => {
    if (!this.rootStore.authStore.user) {
      return;
    }
    const module = this.data.modules[moduleId];
    const newConnectionValue = module.multi
      ? FieldValue.arrayUnion(...(Array.isArray(rowIds) ? rowIds : [rowIds]))
      : Array.isArray(rowIds)
      ? rowIds[0]
      : rowIds;
    if (card) {
      const existingConnections = card.data.connections[moduleId];
      await this.cardListRef.doc(card.id).update({
        [`connections.${moduleId}`]: newConnectionValue,
      });

      const connUser = this.rootStore.authStore.user;
      emitCardEvent(
        this.id,
        card.id,
        'connection.added',
        connUser?.uid ?? '',
        connUser?.email ?? null,
        {
          moduleId,
          rowIds: Array.isArray(rowIds) ? rowIds : [rowIds],
        },
      );

      return {
        undo: async () => {
          await this.cardListRef.doc(card.id).update({
            [`connections.${moduleId}`]: module.multi
              ? FieldValue.arrayRemove(rowIds)
              : typeof existingConnections === 'string'
              ? existingConnections
              : FieldValue.delete(),
          });
        },
        newCardId: undefined,
      };
    }
    const txUser = this.rootStore.authStore.user;
    let cardRef: firebase.firestore.DocumentReference<FirestoreCard> | undefined;
    await firestoreDb.runTransaction(async (transaction) => {
      const newCard = await transaction.get(this.cardListRef.doc());
      cardRef = newCard.ref;
      transaction.set(cardRef, {
        createdAt: FieldValue.serverTimestamp() as unknown as Date,
        createdBy: {
          userId: txUser?.uid ?? '',
          email: txUser?.email ?? null,
        },
        connections: {
          [moduleId]: newConnectionValue as string,
        },
      });

      // Decide where to place the new card
      const cardIdsCopy = [...this.data.cardIds];

      if (position === 'end') {
        await transaction.set(
          this.ref,
          {
            cardIds: FieldValue.arrayUnion(cardRef.id) as unknown as string[],
          },
          { merge: true },
        );
      } else if (position === 'start') {
        cardIdsCopy.splice(this.currentCardIdx, 0, cardRef.id);
        await transaction.set(
          this.ref,
          {
            cardIds: cardIdsCopy,
          },
          { merge: true },
        );
      } else if (afterCardId) {
        const cardIds = Array.isArray(this.data.cardIds) ? this.data.cardIds : [];
        const idx = cardIds.indexOf(afterCardId);
        if (idx === -1) {
          // afterCardId not found, append to end
          cardIdsCopy.push(cardRef.id);
        } else {
          cardIdsCopy.splice(idx + 1, 0, cardRef.id);
        }
        await transaction.set(
          this.ref,
          {
            cardIds: cardIdsCopy,
          },
          { merge: true },
        );
      } else {
        // Default: append to end when no position specified
        await transaction.set(
          this.ref,
          { cardIds: FieldValue.arrayUnion(cardRef.id) as unknown as string[] },
          { merge: true },
        );
      }
    });

    if (cardRef) {
      emitCardEvent(this.id, cardRef.id, 'card.created', txUser?.uid ?? '', txUser?.email ?? null, {
        via: 'connection',
        moduleId,
      });
    }

    // Return both undo function and the new card ID (if a new card was created)
    const newCardId = cardRef?.id;
    const undo = async () => {
      if (cardRef) {
        const undoBatch = firestoreDb.batch();
        undoBatch.delete(cardRef);
        undoBatch.set(
          this.ref,
          {
            cardIds: FieldValue.arrayRemove(cardRef.id) as unknown as string[],
          },
          { merge: true },
        );

        await undoBatch.commit();
      }
    };

    return { undo, newCardId };
  };

  addConnection = async ({
    rowIds,
    moduleId,
    card,
    position,
    afterCardId,
    goToCardFn,
  }: {
    rowIds: string | string[];
    moduleId: string;
    card?: Card;
    position?: 'start' | 'end';
    afterCardId?: string;
    goToCardFn?: (cardId: string) => void;
  }): Promise<string | undefined> => {
    const result = await this.addConnectionOnly({
      rowIds,
      moduleId,
      card,
      position,
      afterCardId,
    });
    if (result !== undefined) {
      const module = this.data.modules[moduleId];
      if (card) {
        const undoCopy = `Item from ${module.name} connected to card #${card.cardNumber}`;
        this.rootStore.uiSnackbarStore.setUndoDetails(result.undo, undoCopy);
      } else if (goToCardFn && result.newCardId) {
        const cardId = result.newCardId;
        this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
          result.undo,
          'New Card Created',
          () => goToCardFn(cardId),
          'Go to Card',
        );
      } else {
        this.rootStore.uiSnackbarStore.setUndoDetails(result.undo, 'New Card Created');
      }
      return result.newCardId;
    }
    return undefined;
  };

  batchRemoveConnections = async ({
    selectedCards,
    moduleId,
    rowId,
  }: {
    selectedCards: Card[];
    rowId: string;
    moduleId: string;
  }) => {
    const undos: Array<() => Promise<void>> = [];

    await Promise.all(
      selectedCards.map(async (card) => {
        card.setModificationState('updating');
        const undo = await card.removeRowOnly(moduleId, rowId);
        card.resetMotificationState();
        if (undo) {
          undos.push(undo);
        }
      }),
    );

    // Increment the connection count for successful removals - removing also saves time
    this.rootStore.uiTimeSavedStore.incrementConnections(undos.length);

    if (undos.length) {
      this.rootStore.uiSnackbarStore.setUndoDetails(
        () =>
          undos.forEach(async (undo) => {
            await undo();
            // Don't decrement on undo since all actions save time
          }),
        // Use undo length because some cards might not have the connection
        `${selectedCards.length} cards updated`,
      );
    }
  };

  batchAddConnections = async ({
    selectedCards,
    ...rest
  }: {
    selectedCards: Card[];
    rowIds: string | string[];
    moduleId: string;
    position?: 'start' | 'end';
    afterCardId?: string;
  }) => {
    const undos: Array<() => Promise<void>> = [];
    let successCount = 0;

    await Promise.all(
      selectedCards.map(async (card) => {
        card.setModificationState('updating');
        const result = await this.addConnectionOnly({
          ...rest,
          card,
        });
        card.resetMotificationState();
        if (result) {
          undos.push(result.undo);
          successCount += 1;
        }
      }),
    );

    // Increment the connection count for successful additions
    if (successCount > 0) {
      const rowCount = Array.isArray(rest.rowIds) ? rest.rowIds.length : 1;
      this.rootStore.uiTimeSavedStore.incrementConnections(successCount * rowCount);
    }

    this.rootStore.uiSnackbarStore.setUndoDetails(
      () =>
        undos.forEach(async (undo) => {
          await undo();
          // Decrement connections on undo
          const rowCount = Array.isArray(rest.rowIds) ? rest.rowIds.length : 1;
          this.rootStore.uiTimeSavedStore.decrementConnections(rowCount);
        }),
      `${selectedCards.length} cards updated`,
    );
  };

  addEmptyConnectionToCard = async (
    card: Card,
    moduleId: string,
    fieldId?: string,
    openFieldEditor: boolean = true,
  ) => {
    const module = this.data.modules[moduleId];
    const source = this.rootStore.sourcesStore.sources[module.sourceId];
    const id = source.addEmptyRow();
    this?.addConnection({
      rowIds: id,
      moduleId: module.id,
      card,
    });
    await waitFor(() => !!source.rows[id]);

    if (openFieldEditor) {
      this.rootStore.uiModuleSourceEditor
        .getRowEditor(module.id, id)
        ?.startEditing(fieldId ?? module.fields[0]);
    }
  };

  upsertSourceToSession = async ({
    source,
    rows,
    sourceId,
    automaticModuleCreation,
  }: {
    source: FirestoreSource;
    rows: RowData[];
    sourceId?: string;
    automaticModuleCreation?: boolean;
  }) => {
    // If sourceId, update source with new data. If no source, create new one.
    const isNewSource = !sourceId;
    const sourceRef = sourceId
      ? db.sessionSources(this.id).doc(sourceId)
      : db.sessionSources(this.id).doc();
    if (isNewSource) {
      trackAction('source_created', source.type === 'image' ? 'image' : 'data');
    }

    await sourceRef.set({
      fieldIds: source.fieldIds,
      fieldNames: source.fieldNames,
      name: source.name,
    });

    if (source.type) {
      await sourceRef.set(
        {
          type: source.type,
        },
        { merge: true },
      );
    }

    if (source.imageFieldIds && source.imageFieldIds.length > 0) {
      await sourceRef.set(
        {
          imageFieldIds: source.imageFieldIds,
        },
        { merge: true },
      );
    }

    // Legacy: sources created before imageFieldIds migration may have attachmentFieldId.
    // Both can coexist; shared logic reads both. New sources use imageFieldIds only.
    if (source.attachmentFieldId) {
      await sourceRef.set({ attachmentFieldId: source.attachmentFieldId }, { merge: true });
    } else if (
      source.type !== 'image' &&
      (!source.imageFieldIds || source.imageFieldIds.length === 0)
    ) {
      // Auto-create image column for text sources without one (use imageFieldIds for consistency)
      const imageFieldId = ulid();
      try {
        await sourceRef.set(
          {
            imageFieldIds: [imageFieldId],
            fieldIds: FieldValue.arrayUnion(imageFieldId) as unknown as string[],
            fieldNames: { [imageFieldId]: 'Image' },
          },
          { merge: true },
        );
      } catch (error) {
        console.error('Failed to create image field:', error);
      }
    }

    const ulidFactory = monotonicFactory();
    const rowsCollection = db.sessionSourceRows(this.id, sourceRef.id);
    // Add rows to source in batches
    await safeBatch(firestoreDb, rows, (batch, row) => {
      const rowDoc = rowsCollection.doc(ulidFactory());
      batch.set(rowDoc, { fieldValues: row });
    });

    // Add source to session's sources
    await this.ref.set(
      {
        sourceIds: FieldValue.arrayUnion(sourceRef.id) as unknown as string[],
      },
      { merge: true },
    );

    if (automaticModuleCreation ?? this.data.automaticModuleCreation) {
      // Check if a module already exists for this source (placeholder modules)
      const existingModule = this.modules.find((m) => m.sourceId === sourceRef.id);

      if (existingModule) {
        // Update existing module instead of creating new one
        console.log(`Updating existing module for source "${source.name}", clearing needsSetup`);
        const isGallery =
          source.displayMode === 'gallery' || (!source.displayMode && source.type === 'image');
        this.upsertModule({
          moduleId: existingModule.id,
          name: source.name,
          sourceId: sourceRef.id,
          fields:
            source.defaultFields && source.defaultFields.length > 0
              ? source.defaultFields
              : source.fieldIds,
          multi: isGallery,
          table: true,
          color: existingModule.color,
        });

        // Explicitly clear needsSetup flag if it was set (if it exists)
        if ((existingModule as any).needsSetup) {
          await this.ref.update({
            [`modules.${existingModule.id}.needsSetup`]: FieldValue.delete(),
          });
        }
      } else {
        // Create new module
        const moduleId = ulid();
        const randomColor = moduleColors[Math.floor(Math.random() * moduleColors.length)];
        const isGallery =
          source.displayMode === 'gallery' || (!source.displayMode && source.type === 'image');
        this.upsertModule({
          moduleId,
          name: source.name,
          sourceId: sourceRef.id,
          fields:
            source.defaultFields && source.defaultFields.length > 0
              ? source.defaultFields
              : source.fieldIds,
          multi: isGallery,
          table: true,
          color: randomColor,
        });
      }
    }

    return sourceRef.id;
  };

  deleteSource = async ({ sourceId }: { sourceId: string }) => {
    trackAction('source_deleted');
    const batch = firestoreDb.batch();

    const sourceRef = db.sessionSources(this.id).doc(sourceId);
    batch.delete(sourceRef);

    batch.set(
      this.ref,
      {
        sourceIds: FieldValue.arrayRemove(sourceId) as unknown as string[],
      },
      { merge: true },
    );

    // Delete all modules that use the deleted source
    for (const moduleId of Object.keys(this.data.modules)) {
      const module = this.data.modules[moduleId];
      if (module.sourceId === sourceId) {
        this.deleteModule({ moduleId: module.id });
      }
    }
    return batch.commit();
  };

  updateSource = ({
    sourceId,
    name,
    emoji,
  }: {
    sourceId: string;
    name?: string;
    emoji?: string;
  }) => {
    const sourceRef = db.sessionSources(this.id).doc(sourceId);
    const updateData: Partial<FirestoreSource> = {};
    if (name !== undefined) {
      updateData.name = name;
      // Only track when the name actually changed — UIs that save on blur
      // would otherwise inflate this counter from no-op saves.
      const existingName = this.rootStore.sourcesStore.sources[sourceId]?.data?.name;
      if (existingName !== name) {
        trackAction('source_renamed');
      }
    }
    if (emoji !== undefined) {
      updateData.emoji = emoji;
    }
    sourceRef.set(updateData, { merge: true });
  };

  // Add or update module for current session
  upsertModule = ({
    moduleId,
    name,
    sourceId,
    multi,
    table,
    color,
    fields,
    showThumbnails,
  }: {
    moduleId: string;
    name: string;
    sourceId: string;
    multi: boolean;
    table: boolean;
    color: string;
    fields?: string[];
    /**
     * @deprecated Image fields are now in the fields array.
     * Only pass this for backwards compat when editing modules that have it set.
     */
    showThumbnails?: boolean;
  }) => {
    const source = this.rootStore.sourcesStore.sources[sourceId];
    const module = this.data.modules[moduleId];

    if (module && module.multi !== true && multi === true) {
      this.makeModuleConnectionsMulti({ moduleId });
    }

    if (source) {
      const moduleData: Record<string, unknown> = {
        id: moduleId,
        name,
        sourceId,
        multi,
        table,
        color,
        fields: fields ?? source.defaultFieldIds,
      };

      // Only include showThumbnails if explicitly provided (backwards compat for old modules)
      // New modules should use the fields array to include image fields
      if (showThumbnails !== undefined) {
        moduleData.showThumbnails = showThumbnails;
      }

      this.ref.update({
        [`modules.${moduleId}`]: moduleData,
        moduleIds: FieldValue.arrayUnion(moduleId),
      });
    }
  };

  deleteModule = ({ moduleId }: { moduleId: string }) => {
    // After deleting module remove, lingering data from cardList
    this.removeModuleConnectionsFromCards({ moduleId });

    // Filter out any items in formulas that might be dependent on module
    const updatedFilenameFormula = (this.data?.filenameFormula ?? []).filter((f) => {
      if (f.type !== 'module') {
        return true;
      }
      return f.type === 'module' && f.moduleId !== moduleId;
    });
    this.saveFilename(updatedFilenameFormula, this.data?.filenameSettings);

    const updatedCardTitleFormula = (this.data?.cardTitleFormula ?? []).filter((f) => {
      if (f.type !== 'module') {
        return true;
      }
      return f.type === 'module' && f.moduleId !== moduleId;
    });
    this.saveCardTitleFormula(updatedCardTitleFormula);

    this.ref.update({
      [`modules.${moduleId}`]: FieldValue.delete(),
      moduleIds: FieldValue.arrayRemove(moduleId),
    });
  };

  // when changing module from single to multi, make the connections on cards into an array
  makeModuleConnectionsMulti = ({ moduleId }: { moduleId: string }) => {
    const batch = firestoreDb.batch();
    (this.data?.cardIds ?? []).forEach((cardId) => {
      const card = this.rootStore.cardsStore.cards[cardId];
      if (card) {
        const moduleConnections = card.getModuleConnections(moduleId);
        if (moduleConnections?.length > 0) {
          const cardRef = this.cardListRef.doc(cardId);
          batch.update(cardRef, {
            [`connections.${moduleId}`]: [moduleConnections[0].id],
          });
        }
      }
    });
    batch.commit();
  };

  // When changing Module source or type from Multi -> Single, we need to remove existing connections from all cards
  removeModuleConnectionsFromCards = ({ moduleId }: { moduleId: string }) => {
    const batch = firestoreDb.batch();
    (this.data?.cardIds ?? []).forEach((cardId) => {
      const cardRef = this.cardListRef.doc(cardId);
      batch.update(cardRef, {
        [`connections.${moduleId}`]: FieldValue.delete(),
      });
    });
    batch.commit();
  };

  switchModuleField = ({ moduleId, field }: { moduleId: string; field: string }) => {
    const module = this.data.modules[moduleId];
    if (module.fields.includes(field)) {
      this.ref.update({
        [`modules.${moduleId}.fields`]: FieldValue.arrayRemove(field),
      });
    } else {
      this.ref.update({
        [`modules.${moduleId}.fields`]: FieldValue.arrayUnion(field),
      });
    }
  };

  switchModuleThumbnailVisibility = ({ moduleId }: { moduleId: string }) => {
    const module = this.data.modules[moduleId];
    this.ref.update({
      [`modules.${moduleId}.showThumbnails`]: !module.showThumbnails,
    });
  };

  switchFilenameVisibility = (newValue: boolean) => {
    this.ref.update({
      'visibility.hideFilename': !newValue,
    });
  };

  toggleModuleVisibility = (id: string) => {
    if (this.getIsModuleVisible(id)) {
      this.ref.update({
        'visibility.hiddenModuleIds': FieldValue.arrayUnion(id),
      });
    } else {
      this.ref.update({
        'visibility.hiddenModuleIds': FieldValue.arrayRemove(id),
      });
    }
  };

  /**
   * Toggle a module's visibility for the layer the card actually reads. When the
   * card is in a step, write the step override (stepLayoutOverrides[stepId].
   * hiddenModuleIds) — the layer getEffectiveHiddenModuleIds / card.visibleModules
   * consume — seeding it from the current effective hidden set so other hides are
   * preserved. Without a step, this is the plain global toggle. Fixes "hide/unhide
   * on a stepped card does nothing" (the global toggle no-ops once a step has its
   * own hiddenModuleIds override, since getEffectiveHiddenModuleIds replaces, not
   * merges).
   */
  toggleStepModuleVisibility = (id: string, stepId?: string | null) => {
    if (!stepId) {
      this.toggleModuleVisibility(id);
      return;
    }
    const next = new Set(this.getEffectiveHiddenModuleIds(stepId));
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.setStepHiddenModuleIds(stepId, [...next]);
  };

  async updatePublicSessionAccessLevel(level: SessionPermissions) {
    const messages = {
      [SessionPermissions.VIEW]: 'Anyone with the link can now view your session',
      [SessionPermissions.EDIT]: 'Anyone with the link can edit now your session',
      [SessionPermissions.PRIVATE]: 'Your session is now private',
    };
    await this.ref.set(
      {
        publicAccessLevel: level,
      },
      { merge: true },
    );
    this.rootStore.uiSnackbarStore.setSnackbarDetails({
      description: messages[level],
    });
  }

  get automaticFilenameVersioning() {
    return this.data.automaticFilenameVersioning ?? true;
  }

  toggleAutomaticFilenameVersioning = () => {
    this.ref.set(
      {
        automaticFilenameVersioning: !this.data.automaticFilenameVersioning,
      },
      {
        merge: true,
      },
    );
  };

  get automaticCardTitleSuffix() {
    return this.data.automaticCardTitleSuffix ?? true;
  }

  toggleAutomaticCardTitleSuffix = () => {
    this.ref.set(
      {
        automaticCardTitleSuffix: !this.data.automaticCardTitleSuffix,
      },
      {
        merge: true,
      },
    );
  };

  toggleAutomaticModuleCreation = () => {
    this.ref.set(
      {
        automaticModuleCreation: !this.data.automaticModuleCreation,
      },
      {
        merge: true,
      },
    );
  };

  // Note: doubleClickBucketsToOpenLibrary was removed from FirestoreSession schema
  // Keeping method for backward compatibility but it always returns true
  get doubleClickBucketsToOpenLibrary() {
    return true;
  }

  toggleDoubleClickBucketsToOpenLibrary = () => {
    // No-op: feature removed from schema, always behaves as if enabled
  };

  saveCardTitleFormula(newFormula: Field[]) {
    this.ref.set(
      {
        cardTitleFormula: newFormula,
      },
      {
        merge: true,
      },
    );
  }

  saveFilename(newFormula: Field[], settings: FilenameSettings) {
    this.ref.set(
      {
        filenameFormula: newFormula,
        filenameSettings: settings,
      },
      {
        merge: true,
      },
    );
  }

  /**
   * Get a card's display number based on its position in the currently active cards,
   * which is race-condition safe during deletion/restoration operations
   */
  getCardDisplayNumber(cardId: string): number {
    // First try to find it in the current cardIds array (normal case)
    const cardIds = Array.isArray(this.data.cardIds) ? this.data.cardIds : [];
    const cardIndex = cardIds.indexOf(cardId);
    if (cardIndex !== -1 && this.rootStore.cardsStore.cards[cardId]) {
      return cardIndex + 1;
    }

    // Fallback: find position within actually loaded active cards (race-condition safe)
    const activeCardIds = (this.data?.cardIds ?? []).filter(
      (id) => !!this.rootStore.cardsStore.cards[id],
    );
    const activeIndex = activeCardIds.indexOf(cardId);
    return activeIndex !== -1 ? activeIndex + 1 : 0;
  }

  /**
   * Load user-specific permissions for this session
   * Skip for guest users (they use custom claims-based access)
   */
  async loadUserPermissions() {
    // Guard against concurrent calls
    if (this.permissionsLoaded || this._permissionsLoading) return;

    this._permissionsLoading = true;

    // Skip loading permissions for guest users - they use custom claims
    const { user } = this.rootStore.authStore;
    if (user?.isGuest) {
      // For guests, mark as loaded immediately since they don't use permission documents
      // But ensure we're in a runInAction to properly update observable
      runInAction(() => {
        this.permissionsLoaded = true;
        this._permissionsLoading = false;
      });
      return;
    }

    // Skip if user is not authenticated
    if (!user) {
      this.permissionsLoaded = true;
      return;
    }

    try {
      // Double-check user is not a guest before attempting to read permission document
      // (defensive check for race conditions where isGuest might not be set yet)
      const currentUser = this.rootStore.authStore.user;
      if (currentUser?.isGuest) {
        this.permissionsLoaded = true;
        this._permissionsLoading = false;
        return;
      }

      // Only load the current user's permission document (users can only read their own)
      const userPermissionDoc = await this.permissionsRef.doc(user.uid).get();
      const permissions: Record<string, FirestoreSessionPermission> = {};

      if (userPermissionDoc.exists) {
        const permissionData = userPermissionDoc.data();
        if (permissionData) {
          // Store by userId (which should match user.uid) for lookup
          permissions[permissionData.userId || user.uid] = permissionData;
          // Also store by user.uid to ensure we can find it
          permissions[user.uid] = permissionData;
        }
      }

      // Wrap observable updates in runInAction since we're in an async function
      runInAction(() => {
        this.userPermissions = permissions;
        this.permissionsLoaded = true;
        this._permissionsLoading = false;
      });

      // Set up a listener to watch for changes to the permission document
      // This handles cases where permissions are granted/revoked after the session is loaded
      // Note: 'user' is already declared at the top of this function
      // Double-check user is not a guest before setting up listener (defensive check for race conditions)
      if (user && !user.isGuest && !this._permissionListener) {
        this._permissionListener = this.permissionsRef.doc(user.uid).onSnapshot(
          (doc) => {
            // Re-check user is still not a guest (in case it changed)
            const currentUser = this.rootStore.authStore.user;
            if (currentUser?.isGuest) {
              // User became a guest, cleanup listener
              if (this._permissionListener) {
                this._permissionListener();
                this._permissionListener = undefined;
              }
              return;
            }

            runInAction(() => {
              const permissions: Record<string, FirestoreSessionPermission> = {
                ...this.userPermissions,
              };

              if (doc.exists) {
                const permissionData = doc.data();
                if (permissionData) {
                  permissions[permissionData.userId || user.uid] = permissionData;
                  permissions[user.uid] = permissionData;
                }
              } else {
                // Permission document was deleted
                delete permissions[user.uid];
                if (this.userPermissions[user.uid]) {
                  const oldUserId = this.userPermissions[user.uid].userId;
                  if (oldUserId) {
                    delete permissions[oldUserId];
                  }
                }
              }

              this.userPermissions = permissions;
            });
          },
          (error) => {
            // If error is permission denied and user is a guest, silently ignore
            // (guests don't have permission documents, they use custom claims)
            const currentUser = this.rootStore.authStore.user;
            if (currentUser?.isGuest && error.code === 'permission-denied') {
              // Cleanup listener since guests don't need it
              if (this._permissionListener) {
                this._permissionListener();
                this._permissionListener = undefined;
              }
              return;
            }
            console.error('[Session] Error listening to permission document:', error);
          },
        );
      }
    } catch (error: any) {
      // If error is permission denied and user is a guest, silently handle it
      // (guests don't have permission documents, they use custom claims)
      const currentUser = this.rootStore.authStore.user;
      if (currentUser?.isGuest && error?.code === 'permission-denied') {
        runInAction(() => {
          this.permissionsLoaded = true;
          this._permissionsLoading = false;
        });
        return;
      }

      console.error('[Session] Failed to load user permissions:', error);
      // Still mark as loaded to avoid infinite retries
      runInAction(() => {
        this.permissionsLoaded = true;
        this._permissionsLoading = false;
      });
    }
  }

  /**
   * Override canViewSession to use user-specific permissions
   */
  get canViewSession() {
    const { user } = this.rootStore.authStore;
    if (!user) return false;
    if (this.isUserOwner(user.uid)) return true;

    // Check guest user custom claims first
    // Use optional chaining to handle race conditions where isGuest might not be set yet
    if (user.isGuest && user.guestSessionId === this.id) {
      // Guest user has access to this specific session
      // Permission level is checked in canEditSession
      return true;
    }

    // For guests, don't check permission documents (they use custom claims)
    // If user appears to be a guest (has guestSessionId but isGuest not set yet), allow access
    // This handles race conditions during initial load
    if (user.guestSessionId === this.id && !this.permissionsLoaded) {
      // Likely a guest user whose isGuest flag hasn't been set yet
      // Allow access based on guestSessionId match
      return true;
    }

    return this.canUserViewSession(user.uid);
  }

  /**
   * Public getter to check if permissions have been loaded
   */
  get arePermissionsLoaded() {
    return this.permissionsLoaded;
  }

  /**
   * Override canEditSession to use user-specific permissions
   */
  get canEditSession() {
    const { user } = this.rootStore.authStore;
    if (!user) return false;
    if (this.isUserOwner(user.uid)) return true;

    // Check guest user custom claims first
    if (user.isGuest && user.guestSessionId === this.id) {
      // Guest user has access to this specific session
      // Check permission level from custom claims (values are lowercase: 'view', 'edit', 'owner')
      const permission = user.guestPermission?.toLowerCase();
      return permission === 'edit' || permission === 'owner';
    }

    // Check user-specific permissions first
    const userPermission = this.getUserPermission(user.uid);
    if (userPermission) {
      const canEdit =
        userPermission === SessionUserPermission.EDIT ||
        userPermission === SessionUserPermission.OWNER;
      if (!canEdit) {
        console.log(
          `[Session] User ${user.uid} has permission ${userPermission} which is not EDIT or OWNER`,
        );
      }
      return canEdit;
    }

    // If permissions haven't loaded yet, fall back to publicAccessLevel
    // Permissions should be loaded eagerly when session is created/attached
    // Don't trigger loading here - computed properties should be pure (no side effects)
    if (!this.permissionsLoaded) {
      // Return false for safety - permissions will be loaded by the time this is checked again
      return false;
    }

    // Permissions loaded but no permission found - check public access level
    console.log(
      `[Session] No user permission found for ${user.uid}, falling back to publicAccessLevel:`,
      this.data.publicAccessLevel,
    );
    return this.data.publicAccessLevel === SessionPermissions.EDIT;
  }

  /**
   * Sets up the cache update reaction that watches for changes in card search,
   * module power column selections, and other store updates.
   * This reaction is recreated when switching back to a session after it was disposed.
   */
  private _setupCacheUpdateReaction() {
    // Dispose existing reaction if any
    if (this._cacheUpdateDisposer) {
      this._cacheUpdateDisposer();
    }

    // Setup cache update reaction
    // Use structural comparison to avoid updating cache on every render
    this._cacheUpdateDisposer = reaction(
      () => {
        const { cards } = this.rootStore.cardsStore;
        const cardIds = this.data?.cardIds?.slice() ?? [];

        // Track individual card visibility states to detect changes in card properties
        // (e.g., title edits, connection changes) that affect visibility
        // Accessing isVisible will automatically track all its dependencies via MobX
        const cardVisibilityStates = cardIds.map((cardId) => {
          const card = cards[cardId];
          // Return a simple object with cardId and visibility state
          // This ensures the reaction triggers when any card's visibility changes
          return card ? { cardId, isVisible: card.isVisible } : { cardId, isVisible: false };
        });

        return {
          cardIds,
          hasSearch: this.rootStore.uiCardSearchStore.hasSearch,
          searchTerms: this.rootStore.uiCardSearchStore.searchTerms.slice(),
          hasRowSelection: this.rootStore.uiModulePowerColumnStore.hasRowSelection,
          // Track both module IDs and the actual selected row IDs within each module
          rowSelections: Object.entries(
            this.rootStore.uiModulePowerColumnStore.rowSelectionsByModuleId,
          ).map(([moduleId, rowSet]) => ({
            moduleId,
            rows: Array.from(rowSet).sort(),
          })),
          cardsCount: Object.keys(cards).length,
          // Track card visibility states to detect property changes that affect visibility
          cardVisibilityStates,
        };
      },
      () => {
        this.updateCache();
      },
      { equals: comparer.structural },
    );
  }

  /**
   * Recreates the cache update reaction if it was previously disposed.
   * This is called when switching back to a session that had its reaction disposed.
   */
  ensureReactionActive() {
    if (!this._cacheUpdateDisposer) {
      this._setupCacheUpdateReaction();
    }
  }

  /**
   * Dispose of this session's reactions and cleanup resources
   */
  dispose() {
    if (this._cacheUpdateDisposer) {
      this._cacheUpdateDisposer();
      this._cacheUpdateDisposer = undefined;
    }
  }

  // ============================================
  // Multi-Step Queue Infrastructure
  // ============================================

  /**
   * Move a card to a different step
   * Updates the card's stepId and stepEnteredAt, and creates an audit trail entry
   *
   * @param cardId - The card to move
   * @param stepId - The target step ID (null for unassigned/triage)
   */
  async moveCardToStep(cardId: string, stepId: string | null, silent = false) {
    const { user } = this.rootStore.authStore;
    if (!user) return;

    const card = this.rootStore.cardsStore.cards[cardId];
    if (!card) return;

    const previousStepId = card.data.stepId ?? null;

    // Don't do anything if already in this step
    if (previousStepId === stepId) return;

    const now = FieldValue.serverTimestamp() as unknown as Date;
    const reorderedCardIds = getCardIdsWithStepMoveFirst(
      this.data.cardIds ?? [],
      Object.fromEntries(
        (this.data.cardIds ?? []).map((id) => [
          id,
          { stepId: this.rootStore.cardsStore.cards[id]?.data.stepId ?? null },
        ]),
      ),
      cardId,
      stepId,
    );

    // Start a batch operation
    const batch = firestoreDb.batch();

    // 1. Update the card's stepId and stepEnteredAt
    const cardRef = this.cardListRef.doc(cardId);
    batch.update(cardRef, {
      stepId: stepId,
      stepEnteredAt: now,
    });

    // 2. Close out the previous step history entry (if any)
    if (previousStepId !== null) {
      // Find the most recent step history entry for the previous step
      const stepHistoryRef = db.cardStepHistory(this.id, cardId);
      const previousEntries = await stepHistoryRef
        .where('stepId', '==', previousStepId)
        .where('exitedAt', '==', null)
        .orderBy('enteredAt', 'desc')
        .limit(1)
        .get();

      if (!previousEntries.empty) {
        const previousEntry = previousEntries.docs[0];
        batch.update(previousEntry.ref, {
          exitedAt: now,
        });
      }
    }

    // 3. Create a new step history entry (only if moving to a real step, not unassigned)
    if (stepId !== null) {
      const stepHistoryRef = db.cardStepHistory(this.id, cardId);
      const newHistoryEntry: StepHistoryEvent = {
        stepId: stepId,
        enteredAt: now,
        userId: user.uid,
        userEmail: user.email,
      };
      batch.set(stepHistoryRef.doc(), newHistoryEntry);
    }

    batch.set(
      this.sessionRef,
      {
        cardIds: reorderedCardIds,
      },
      { merge: true },
    );

    await batch.commit();

    emitCardEvent(this.id, cardId, 'card.movedToStep', user.uid, user.email, {
      from: previousStepId,
      to: stepId,
    });

    // Show snackbar with undo + navigate-to-step actions (unless called from batch)
    if (!silent) {
      const stepName = getMoveStepName({
        stepId,
        steps: this.steps,
        getStepNameById: (targetStepId) =>
          this.rootStore.uiActiveSessionStore.getStepNameById(targetStepId),
        triageStepId: STEP_TAB_TRIAGE,
      });
      const navStepId = stepId ?? STEP_TAB_TRIAGE;
      this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
        () => this.moveCardToStep(cardId, previousStepId),
        `Card moved to ${stepName}`,
        () => this.rootStore.uiActiveSessionStore.navigateToStep(navStepId),
        'Visit Card',
      );
    }
  }

  /**
   * Move multiple cards to a step
   * Uses Promise.allSettled to handle partial failures gracefully
   */
  async moveCardsToStep(cardIds: string[], stepId: string | null) {
    const uniqueCardIds = [...new Set(cardIds)];
    const previousStepIds = uniqueCardIds.map(
      (id) => this.rootStore.cardsStore.cards[id]?.data.stepId ?? null,
    );
    const results: PromiseSettledResult<void>[] = [];
    const cardIdsToMove = [...uniqueCardIds].reverse();

    for (const cardId of cardIdsToMove) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await this.moveCardToStep(cardId, stepId, true);
        results.push({ status: 'fulfilled', value: undefined });
      } catch (error) {
        results.push({ status: 'rejected', reason: error });
      }
    }

    const failures = results.filter((r) => r.status === 'rejected');

    const stepName = getMoveStepName({
      stepId,
      steps: this.steps,
      getStepNameById: (targetStepId) =>
        this.rootStore.uiActiveSessionStore.getStepNameById(targetStepId),
      triageStepId: STEP_TAB_TRIAGE,
    });
    const navStepId = stepId ?? STEP_TAB_TRIAGE;

    if (failures.length > 0) {
      const successCount = getBatchMoveSuccessCount({
        uniqueCardIds,
        failureCount: failures.length,
      });
      console.error(
        `Failed to move ${failures.length} of ${uniqueCardIds.length} cards:`,
        failures.map((f) => (f as PromiseRejectedResult).reason),
      );
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description:
          failures.length === uniqueCardIds.length
            ? 'Failed to move cards. Please try again.'
            : `Moved ${successCount} of ${uniqueCardIds.length} cards to ${stepName}. ${failures.length} failed.`,
      });
    } else {
      // All succeeded — show consolidated toast with undo + navigate
      const count = uniqueCardIds.length;
      this.rootStore.uiSnackbarStore.setUndoDetailsWithVisitAsset(
        async () => {
          // Undo: move each card back to its original step (silent: true avoids overwriting this toast)
          for (let i = uniqueCardIds.length - 1; i >= 0; i--) {
            // eslint-disable-next-line no-await-in-loop
            await this.moveCardToStep(uniqueCardIds[i], previousStepIds[i], true);
          }
        },
        count === 1 ? `Card moved to ${stepName}` : `${count} cards moved to ${stepName}`,
        () => this.rootStore.uiActiveSessionStore.navigateToStep(navStepId),
        count === 1 ? 'Visit Card' : 'Visit Cards',
      );
    }
  }

  /**
   * Create a new step in the workflow
   */
  async createStep(name: string): Promise<string> {
    trackAction('step_created');
    const stepId = ulid();
    const currentSteps = this.steps;
    const newPosition = currentSteps.length;

    const newStep: SessionStep = {
      id: stepId,
      name,
      position: newPosition,
    };

    await this.ref.update({
      steps: FieldValue.arrayUnion(newStep) as unknown as SessionStep[],
    });

    return stepId;
  }

  /**
   * Update a step's name
   */
  async updateStepName(stepId: string, newName: string) {
    const currentSteps = [...this.steps];
    const stepIndex = currentSteps.findIndex((s) => s.id === stepId);

    if (stepIndex === -1) return;

    // Only track real renames so blur-save / re-save UIs don't inflate the counter.
    if (currentSteps[stepIndex].name !== newName) {
      trackAction('step_renamed');
    }

    currentSteps[stepIndex] = {
      ...currentSteps[stepIndex],
      name: newName,
    };

    await this.ref.update({ steps: currentSteps });
  }

  /**
   * Delete a step from the workflow
   * Cards in this step will become unassigned (stepId = null)
   */
  async deleteStep(stepId: string) {
    trackAction('step_deleted');
    const currentSteps = this.steps.filter((s) => s.id !== stepId);

    // Recalculate positions
    const reorderedSteps = currentSteps.map((step, index) => ({
      ...step,
      position: index,
    }));

    await this.ref.update({
      steps: reorderedSteps,
      // Remove per-step layout override if present (avoids orphan keys in Firestore)
      [`stepLayoutOverrides.${stepId}`]: FieldValue.delete(),
    });

    // Move cards in this step to unassigned
    const cardsInStep = this.getCardIdsByStepId(stepId);
    if (cardsInStep.length > 0) {
      await this.moveCardsToStep(cardsInStep, null);
    }
  }

  /**
   * Reorder steps by providing the new order of step IDs
   */
  async reorderSteps(newOrder: string[]) {
    const currentSteps = this.steps;
    const reorderedSteps = newOrder
      .map((id, index) => {
        const step = currentSteps.find((s) => s.id === id);
        if (!step) return null;
        return { ...step, position: index };
      })
      .filter((s): s is SessionStep => s !== null);

    await this.ref.update({ steps: reorderedSteps });
  }

  // Layout Preset Methods
  getLayoutPresets(): Record<string, any> {
    return (this.data as any).layoutPresets || {};
  }

  getCurrentlyAppliedPresetId(): string | null {
    return (this.data as any).currentlyAppliedPresetId || null;
  }

  async createLayoutPreset(name: string, presetData: any) {
    const presetId = ulid();
    const preset = {
      // Defaults to ensure older/partial presets don't break the UI
      moduleIds: [],
      visibility: {
        hiddenModuleIds: [],
        hideFilename: false,
      },
      moduleDisplaySettings: {},
      connectionDisplaySettings: {},
      ...presetData,
      name,
      createdAt: new Date(),
    };
    await this.ref.update({
      [`layoutPresets.${presetId}`]: preset,
    });
  }

  async updateLayoutPreset(presetId: string, presetData: any) {
    const existingPreset = this.getLayoutPresets()[presetId];
    if (!existingPreset) {
      throw new Error(`Preset ${presetId} not found`);
    }
    const nextPreset = {
      // Preserve anything we don't explicitly update (critical for moduleDisplaySettings)
      ...existingPreset,
      ...presetData,
      // Preserve original creation timestamp
      createdAt: existingPreset.createdAt,
    };
    await this.ref.update({
      [`layoutPresets.${presetId}`]: {
        ...nextPreset,
      },
    });
  }

  async applyLayoutPreset(presetId: string) {
    const presets = this.getLayoutPresets();
    const preset = presets[presetId];
    if (!preset) {
      throw new Error(`Preset ${presetId} not found`);
    }

    // Apply module order
    if (preset.moduleIds) {
      await this.setModuleIds(preset.moduleIds);
    }

    // Apply visibility settings
    if (preset.visibility) {
      await this.ref.update({
        'visibility.hiddenModuleIds': preset.visibility.hiddenModuleIds || [],
        'visibility.hideFilename': preset.visibility.hideFilename ?? false,
      });
    }

    // Apply module display settings
    if (preset.moduleDisplaySettings) {
      const updates: any = {};
      for (const [moduleId, settings] of Object.entries(preset.moduleDisplaySettings)) {
        updates[`moduleDisplaySettings.${moduleId}`] = settings;
      }
      if (Object.keys(updates).length > 0) {
        await this.ref.update(updates);
      }
    }

    // Mark as currently applied
    await this.ref.update({
      currentlyAppliedPresetId: presetId,
    });
  }

  async deleteLayoutPreset(presetId: string) {
    await this.ref.update({
      [`layoutPresets.${presetId}`]: FieldValue.delete(),
      // Clear currentlyAppliedPresetId if this was the applied preset
      ...((this.getCurrentlyAppliedPresetId() === presetId
        ? { currentlyAppliedPresetId: FieldValue.delete() }
        : {}) as any),
    });
  }

  setAssetTabDefaultSources(sourceIds: string[]): void {
    this.ref.update({
      'assetTabPreferences.defaultSourceIds': sourceIds,
    });
  }
}
